import { Directive, input } from '@angular/core';
import { classes } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const alertVariants = cva(
  // Actions (button/link) always sit in the text column below the description —
  // otherwise auto-placement drops them into the icon column and widens it,
  // pushing title and description to the right.
  "grid gap-0.5 rounded-lg border px-4 py-3 text-start text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>ng-icon]:grid-cols-[auto_1fr] has-[>ng-icon]:gap-x-2.5 *:[ng-icon]:row-span-2 *:[ng-icon]:translate-y-0.5 *:[ng-icon]:text-current *:[ng-icon:not([class*='text-'])]:text-[length:--spacing(4)] has-[>ng-icon]:[&>button]:col-start-2 has-[>ng-icon]:[&>a]:col-start-2 [&>button]:justify-self-start [&>a]:justify-self-start group/alert relative w-full",
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        // Neutral status note — highlighted without implying an error.
        info: 'bg-primary/5 border-primary/20 text-foreground *:[ng-icon]:text-primary',
        /**
         * Something needs the user's attention but nothing is broken: a step
         * that is still open, a limit that is close. Use this instead of
         * `destructive` whenever the user can simply go and do the thing —
         * red is reserved for things that actually failed.
         */
        warning: 'bg-warning/10 border-warning/35 text-foreground *:[ng-icon]:text-warning',
        // Something failed or is lost — an error, not a to-do.
        destructive:
          'text-destructive bg-card *:data-[slot=alert-description]:text-destructive/90 *:[ng-icon]:text-current',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type AlertVariants = VariantProps<typeof alertVariants>;

@Directive({
  selector: 'hlm-alert,[hlmAlert]',
  host: {
    'data-slot': 'alert',
    role: 'alert',
  },
})
export class HlmAlert {
  public readonly variant = input<AlertVariants['variant']>('default');

  constructor() {
    classes(() => alertVariants({ variant: this.variant() }));
  }
}
